import { api } from '@/lib/apiClient';

/**
 * FR-NAV-03/04/07/08: what Home shows for the signed-in person and which daily items their role pins to the left menu.
 * `cards` is the final, server-resolved order of Home cards; time-sensitive cards are always in it.
 */
export interface HomeLayout {
  roleCards: string[];
  rolePins: string[];
  hiddenCards: string[];
  cardOrder: string[];
  shortcuts: string[];
  cards: string[];
}

export interface HomePreferences {
  hiddenCards: string[];
  cardOrder: string[];
  shortcuts: string[];
}

/** FR-NAV-07: the Home cards and left-menu pins an admin set for one role. */
export interface RoleHomeDefault {
  roleId: number;
  roleName: string;
  cards: string[];
  pinnedItems: string[];
  /** True while the role uses the built-in default (nothing saved for it). */
  isDefault: boolean;
}

export interface RoleHomeDefaultInput {
  cards: string[];
  pinnedItems: string[];
}

export const HOME_LAYOUT_KEY = 'home-layout';
export const HOME_ROLE_DEFAULTS_KEY = 'home-role-defaults';

/** FR-NAV-03/07: most shortcuts a person pins on Home, most daily items an admin pins to a role's left menu. */
export const MAX_SHORTCUTS = 8;
export const MAX_ROLE_PINS = 12;

/** One Home card as the Customise dialog and the role defaults page name it. */
export interface HomeCardMeta {
  key: string;
  label: string;
  /** FR-NAV-08: time-sensitive cards can be moved but never hidden. */
  timeSensitive?: boolean;
  /** Visible when the person holds any of these (empty = everyone). */
  perms?: string[];
}

export const HOME_CARDS: HomeCardMeta[] = [
  { key: 'approvals', label: 'Approvals waiting', timeSensitive: true, perms: ['Vendor.Approve', 'Hotel.Approve', 'Transport.Approve', 'Customer.Approve'] },
  { key: 'followups-today', label: 'My follow-ups today', timeSensitive: true, perms: ['Lead.View', 'Lead.ViewTeam', 'Lead.ViewAll'] },
  { key: 'transport-expiry', label: 'Transport rate cards ending soon', timeSensitive: true, perms: ['Transport.View'] },
  { key: 'guided-setup', label: 'Guided setup' },
  { key: 'masters-attention', label: 'Masters needing attention' },
  { key: 'my-leads', label: 'My leads by stage', perms: ['Lead.View', 'Lead.ViewTeam', 'Lead.ViewAll'] },
  { key: 'shortcuts', label: 'My shortcuts' },
];

/** The built-in order, used while the layout loads or when it cannot be read. */
export const DEFAULT_HOME_CARDS = HOME_CARDS.map((c) => c.key);

export const homeCardMeta = (key: string) => HOME_CARDS.find((c) => c.key === key);

export const homeApi = {
  layout: () => api.get<HomeLayout>('/home/layout'),
  savePreferences: (body: HomePreferences) => api.put<HomeLayout>('/home/preferences', body),
  roleDefaults: () => api.get<RoleHomeDefault[]>('/home/role-defaults'),
  saveRoleDefault: (roleId: number, body: RoleHomeDefaultInput) => api.put<RoleHomeDefault>(`/home/role-defaults/${roleId}`, body),
  resetRoleDefault: (roleId: number) => api.del<RoleHomeDefault>(`/home/role-defaults/${roleId}`),
};
